import { ExclusionReason } from 'src/app/models/clinical-eligibility.model';

/**
 * BRD-CLIN-2026-006 §8.2 — turning the five guide answers into the attestation POST.
 * =========================================================================================
 * PURE. No Angular, no HttpClient, so the mapping that decides whether a patient may be titrated is
 * unit-testable without a component harness. The component owns reading the form; this owns what the
 * answers MEAN.
 *
 * ## IT MIRRORS THE DTO'S REFUSALS ON PURPOSE
 *
 * `AttestEligibilityDto` and `attest()` reject four shapes. Only ONE of them is something this can
 * usefully pre-empt: `all_criteria_reviewed !== true`, the unfinished form. That is not an error, it
 * is simply not ready, so nothing is posted and nothing is said.
 *
 * THE eGFR CONTRADICTION IS NOT GUARDED HERE, and that is a correction rather than an omission. An
 * earlier version refused `egfr_value < 30` with `egfr_lt_30` undeclared, on the reasoning that the
 * clinician had unticked a box that self-ticked from the number beside it. They cannot: welby-web
 * renders an `auto_check` box with `pointer-events: none` and `tabIndex: -1`, and `applyAutoChecks`
 * re-reconciles on every `valueChanges` — including the cache restore, which runs after
 * `setupAutoChecks`. The state was unreachable by every route, so the guard was dead code defending
 * a scenario that reads plausible and cannot happen. Raised by Valentina noticing the boxes are not
 * clickable. If the shape ever does arrive, `attest()` still refuses it and the caller reports it.
 *
 * The remaining two — an unreadable diagnosis record, and an ICD-inferred exclusion that attestation
 * cannot clear — are facts only the server holds. They stay server-side and surface as errors.
 *
 * ## `Unknown` IS NOT AN EXCLUSION, AND NOT A CLEAN SCREEN EITHER
 *
 * Only an explicit "Yes" maps to a reason. "Unknown" means the clinician could not establish the
 * fact, which must be recorded as neither. It is `all_criteria_reviewed` that the clinician withholds
 * while anything is Unknown — so an Unknown answer surfaces as "not ready", never as eligibility.
 */

/** Field ids, mirroring `SeedChfDiureticScreen/diureticScreenFields.js`. */
export const SCREEN_FIELD_LVAD = 'chf_screen_lvad';
export const SCREEN_FIELD_ESRD = 'chf_screen_esrd';
export const SCREEN_FIELD_EGFR = 'chf_screen_egfr';
export const SCREEN_FIELD_EGFR_LOW = 'chf_screen_egfr_low';
export const SCREEN_FIELD_REVIEWED = 'chf_screen_reviewed';

/** The `screen_gate` marker the seed stamps on all five. */
export const DIURETIC_SCREEN_GATE = 'diuretic_exclusion';

export interface DiureticAttestationPayload {
  reasons: ExclusionReason[];
  all_criteria_reviewed: boolean;
  egfr_value?: number;
}

export interface DiureticAttestation {
  /** The body to POST, or null when there is nothing to record yet. */
  payload: DiureticAttestationPayload | null;
  /**
   * Why there is no payload. Absent when there is one.
   *
   * One value, and the caller stays SILENT on it: an unfinished screening block is how most calls
   * end, and nagging about it would train the MA to ignore the warning that matters. There is no
   * `blocking` flag any more — the one case that warranted speaking up was the eGFR contradiction,
   * which turned out to be unreachable (see the module header).
   */
  reason?: 'not_reviewed';
}

/** Reads a number the way the DTO will: anything else is absent, never 0. */
const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

/**
 * Build the POST body from the five answers, or say why there isn't one.
 *
 * `values` is keyed by field id, exactly as the form holds them.
 */
export const buildDiureticAttestation = (values: Record<string, unknown>): DiureticAttestation => {
  const reasons: ExclusionReason[] = [];
  // ONLY an explicit "Yes". `Unknown`, blank and anything unrecognised all decline to assert an
  // exclusion — and equally decline to assert its absence, which `all_criteria_reviewed` carries.
  if (values[SCREEN_FIELD_LVAD] === 'Yes') {
    reasons.push(ExclusionReason.LVAD);
  }
  if (values[SCREEN_FIELD_ESRD] === 'Yes') {
    reasons.push(ExclusionReason.ESRD);
  }
  if (values[SCREEN_FIELD_EGFR_LOW] === true) {
    reasons.push(ExclusionReason.EGFR_LT_30);
  }

  if (values[SCREEN_FIELD_REVIEWED] !== true) {
    // The commonest case by far — the MA has not reached the end of the section. Silent.
    return { payload: null, reason: 'not_reviewed' };
  }

  const egfr = asNumber(values[SCREEN_FIELD_EGFR]);
  const payload: DiureticAttestationPayload = { reasons, all_criteria_reviewed: true };
  // SENT ONLY WHEN THE DTO WILL TAKE IT: `@IsInt() @Min(0) @Max(200)`. A typed `28.5` is a valid
  // clinical reading and an invalid `egfr_value`, so it is OMITTED rather than rounded — rounding
  // would file a number the clinician did not enter as "the value the screen was decided on", and
  // dropping it costs only stored evidence: the verdict rides on `reasons`, which still carries the
  // exclusion. Out-of-range likewise.
  if (egfr !== null && Number.isInteger(egfr) && egfr >= 0 && egfr <= 200) {
    payload.egfr_value = egfr;
  }
  return { payload };
};
